// おまもりガチャ・錬成の演出（有料ではないので派手でよい。GAME_DESIGN 18章）。ファイル名・omk- の名前は互換のため旧名のまま。
// お守り（錬成は香炉）が光る → はじける → 手に入れた景品の絵・名前・レア度・効果をそのまま見せる（レア度に応じた光：極＝金と朱の爆発と紙吹雪、優＝紫、良＝青）。
// 確定演出：極（名物を含む）が含まれるとお守りが金色に光り、鈴の音（audio の omikujiSuzu）。10連はまとめて並べてから1枚ずつめくる。
// 画面を押すと次へ、「スキップ」で結果へ、「最後だけ見る」で最後の1枚だけ演出する。CSS アニメ＋紙吹雪だけ Canvas（30fps に間引き）。
import { EQUIPMENT, OMAMORI, affixText, omamoriMainText, omamoriSubText, type GachaItem } from '@hyakki/core';
import { gachaArt, itemIconStyle, omikujiArt, OMK_ICON } from '../assets';
import { sfx, type SfxKey } from '../audio';
import { confetti as popConfetti } from './confetti';
import { esc, fmt, resIcon } from './dom';
import { omName, omThumbHtml, rarityLabel } from './omamori-view';

export type OmkPhase = 'shake' | 'pop' | 'light' | 'card' | 'grid' | 'flip' | 'result';

/** 演出の格：1 ふつう・2 良・3 優・4 極（名物も4） */
export function itemTier(x: GachaItem): number {
  if (x.kind === 'omamori') return { n: 1, r: 2, sr: 3, ssr: 4 }[x.omamori.rarity] ?? 1;
  if (x.kind === 'equipment') return x.meibutsu ? 4 : x.drop.rarity === 'sr' ? 3 : 2;
  if (x.kind === 'fuda') return 2;
  return 1;
}

/** 景品の名前 */
export function prizeName(x: GachaItem): string {
  switch (x.kind) {
    case 'omamori':
      return `${omName(x.defId)}`;
    case 'equipment':
      return `${x.meibutsu ? '名物 ' : ''}${EQUIPMENT[x.drop.defId]?.name ?? x.drop.defId}`;
    case 'fuda':
      return `式神の札 ×${x.amount}`;
    case 'zaimoku':
      return `材木 ×${fmt(x.amount)}`;
    case 'zeni':
      return `銭 ×${fmt(x.amount)}`;
    case 'exp':
      return `主EXP ${fmt(x.amount)}`;
  }
}

/** 景品の絵 */
export function prizeArt(x: GachaItem, size: 's' | 'm' | 'l' = 'm'): string {
  if (x.kind === 'omamori') return omThumbHtml({ ...x.omamori }, { size, label: size !== 'l' });
  if (x.kind === 'equipment') return `<span class="omk-pz omt-${size} r-${x.meibutsu ? 'ssr' : x.drop.rarity}"><span class="omt-plate" style="${omikujiArt.daishi(x.meibutsu ? 'ssr' : x.drop.rarity)}"></span><span class="omk-pz-it thumb it" style="${itemIconStyle(x.drop.defId)}"></span></span>`;
  const k = x.kind === 'exp' ? 'exp' : x.kind;
  return `<span class="omk-pz omt-${size} r-n"><span class="omt-plate" style="${omikujiArt.daishi('n')}"></span><span class="omk-pz-res">${resIcon(k)}</span></span>`;
}

/** 種類とレア度（お守り：極・ピックアップ／装備：優・装備）。銭などの資源・経験値は名前でわかるので出さない */
const sub = (x: GachaItem) =>
  x.kind === 'omamori'
    ? `お守り・${rarityLabel(x.omamori.rarity)}${x.pickup ? '・ピックアップ' : ''}`
    : x.kind === 'equipment'
      ? `装備・${rarityLabel(x.meibutsu ? 'ssr' : x.drop.rarity)}${x.meibutsu ? '・名物' : ''}`
      : '';
/** 種類の小さな行（無ければ出さない） */
const subHtml = (x: GachaItem): string => (sub(x) ? `<small>${esc(sub(x))}</small>` : '');

/** 効果の短い説明（一覧用：お守り＝主効果、装備＝追加効果） */
function prizeEffect(x: GachaItem): string {
  if (x.kind === 'omamori') {
    const d = OMAMORI[x.defId];
    return d ? omamoriMainText(d, x.omamori.q) : '';
  }
  if (x.kind === 'equipment') return x.drop.affixes.map((a) => affixText(a)).join('・');
  return '';
}

/** 景品の説明（お守り＝主効果と副効果、装備＝追加効果） */
function prizeDesc(x: GachaItem): string {
  if (x.kind === 'omamori') {
    const d = OMAMORI[x.defId];
    const subs = x.omamori.subs.map((s) => esc(omamoriSubText(s)));
    return `<span class="omk-prize-desc"><b>${esc(d ? omamoriMainText(d, x.omamori.q) : '')}</b>${subs.length ? `<span>${subs.join('　')}</span>` : ''}</span>`;
  }
  if (x.kind === 'equipment' && x.drop.affixes.length) return `<span class="omk-prize-desc"><span>${x.drop.affixes.map((a) => esc(affixText(a))).join('　')}</span></span>`;
  return '';
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const LAST_ONLY_KEY = 'hk.omkLastOnly';

export interface PlayOpts {
  free?: boolean;
  /** お守りに添えた銭の合計（結果に出す） */
  zeni?: number;
  /** 無料の1回で付いた今日のボーナス（結果に出す） */
  bonus?: string;
  /** 錬成の演出（お守りの代わりに香炉） */
  fuse?: boolean;
  /** 撮影・確認用：この段で止める */
  holdAt?: OmkPhase;
  /** 結果の「もう一度」 */
  again?: { label: string; disabled?: boolean; run: () => void };
}

/** 演出を再生して、閉じたら解決する */
export function playOmikuji(items: GachaItem[], o: PlayOpts = {}): Promise<void> {
  return new Promise((resolve) => {
    const ten = items.length > 1;
    const best = Math.max(...items.map(itemTier));
    const gold = best >= 4;
    let lastOnly = false;
    try {
      lastOnly = localStorage.getItem(LAST_ONLY_KEY) === '1';
    } catch {
      /* 保存できなくても動く */
    }
    const el = document.createElement('div');
    el.className = `omk-fx ${ten ? 'ten' : 'one'} ${gold ? 'gold' : ''} ${o.fuse ? 'fuse' : ''}`;
    el.innerHTML = `<div class="omk-bg"></div><div class="omk-rays"></div>
      <div class="omk-stage">
        <div class="omk-boxwrap"><span class="omk-boxglow"></span>${
          o.fuse ? `<span class="omk-box kouro" style="${omikujiArt.icon(OMK_ICON.fuse)}"></span>` : `<span class="omk-box charm" style="${gachaArt.omamori}"></span>`
        }</div>
        <div class="omk-single" hidden></div>
        <ul class="omk-grid" hidden></ul>
      </div>
      <div class="omk-ctl">
        ${ten ? `<label class="omk-last"><input type="checkbox" data-last ${lastOnly ? 'checked' : ''}>最後だけ見る</label>` : ''}
        <button class="btn small omk-skip" data-skip>スキップ</button>
      </div>
      <p class="omk-hint">画面を押すと進みます</p>
      <div class="omk-result" hidden></div>`;
    document.body.appendChild(el);
    const stage = el.querySelector<HTMLElement>('.omk-stage')!;
    const single = el.querySelector<HTMLElement>('.omk-single')!;
    const grid = el.querySelector<HTMLUListElement>('.omk-grid')!;
    const result = el.querySelector<HTMLElement>('.omk-result')!;
    let skip = reduced();
    let tapWake: (() => void) | null = null;
    let holding = false;
    const phase = (p: OmkPhase) => {
      el.dataset.phase = p;
    };
    /** ms 待つ。画面を押すと早く進む。スキップ中は待たない。holdAt の段では止まる */
    const step = async (p: OmkPhase, ms: number) => {
      phase(p);
      if (o.holdAt === p) {
        holding = true;
        await new Promise<void>(() => {});
      }
      // 確認用に途中の段で止めるときは、それより前の段は待たない
      if (skip || o.holdAt) return;
      await Promise.race([wait(ms), new Promise<void>((r) => (tapWake = r))]);
      tapWake = null;
    };
    const play = (k: SfxKey) => !skip && sfx(k);

    el.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-skip]')) {
        skip = true;
        tapWake?.();
        return;
      }
      const last = t.closest<HTMLInputElement>('[data-last]');
      if (last) {
        lastOnly = last.checked;
        try {
          localStorage.setItem(LAST_ONLY_KEY, lastOnly ? '1' : '0');
        } catch {
          /* 保存できなくても動く */
        }
        return;
      }
      if (t.closest('.omk-result')) return;
      tapWake?.();
    });

    const close = () => {
      window.removeEventListener('hashchange', close);
      el.classList.add('out');
      setTimeout(() => el.remove(), 250);
      resolve();
    };

    const lightHtml = (t: number) =>
      t >= 4
        ? `<span class="omk-light l4"><i style="${omikujiArt.fx(0)}"></i><i style="${omikujiArt.fx(3)}"></i><i style="${omikujiArt.fx(15)}"></i></span>`
        : t === 3
          ? `<span class="omk-light l3"><i style="${omikujiArt.fx(4)}"></i><i style="${omikujiArt.fx(5)}"></i></span>`
          : t === 2
            ? `<span class="omk-light l2"><i style="${omikujiArt.fx(8)}"></i><i style="${omikujiArt.fx(9)}"></i></span>`
            : '';
    const burst = (t: number) => {
      if (t >= 4) {
        play('omikujiSSR');
        // 極は紙吹雪（式神を引いたときと同じ、左右下から舞う色紙）
        if (!skip) popConfetti({ top: true });
        el.classList.remove('flash');
        void el.offsetWidth;
        el.classList.add('flash');
      } else if (t === 3) play('omikujiSR');
      else if (t === 2) play('omikujiR');
    };

    const cardHtml = (x: GachaItem, i: number) => {
      const t = itemTier(x);
      return `<li class="omk-card t${t}" data-i="${i}" style="--d:${i * 40}ms">
        <div class="omk-flip">
          <div class="omk-back"><span class="omk-back-mark" style="${gachaArt.omamori}"></span></div>
          <div class="omk-front">${lightHtml(t)}
            <span class="omk-front-art">${prizeArt(x, 's')}</span><b class="omk-front-name">${esc(prizeName(x))}</b>${subHtml(x)}</div>
        </div></li>`;
    };

    const showResult = () => {
      phase('result');
      el.classList.add('done');
      const list = items
        .map((x) => {
          const fx = prizeEffect(x);
          return `<li class="omk-res t${itemTier(x)}">${prizeArt(x, 's')}<span><b>${esc(prizeName(x))}</b>${subHtml(x)}${fx ? `<small class="omk-res-fx">${esc(fx)}</small>` : ''}</span></li>`;
        })
        .join('');
      result.hidden = false;
      result.innerHTML = `<div class="omk-res-card panel-paper">
          <h2 class="sec-title">${o.fuse ? '錬成の結果' : o.free ? '無料ガチャの結果' : ten ? '10連の結果' : '結果'}</h2>
          <ul class="omk-res-list ${ten ? 'ten' : ''}">${list}</ul>${o.zeni ? `<p class="omk-res-bonus"><b>銭</b>+${fmt(o.zeni)}</p>` : ''}${o.bonus ? `<p class="omk-res-bonus"><b>今日のボーナス</b>${esc(o.bonus)}</p>` : ''}
          <div class="btn-row">${o.again ? `<button class="btn primary" data-again ${o.again.disabled ? 'disabled' : ''}>${esc(o.again.label)}</button>` : ''}<button class="btn" data-close>閉じる</button></div>
        </div>`;
      result.querySelector('[data-close]')!.addEventListener('click', close);
      result.querySelector('[data-again]')?.addEventListener('click', () => {
        close();
        o.again!.run();
      });
    };

    // 画面を移ったら閉じる（結果は所持品に入っている）
    window.addEventListener('hashchange', close);

    void (async () => {
      // 1. お守りが光る（確定演出：金色の光・鈴）
      if (gold) {
        el.classList.add('kakutei');
        play('omikujiSuzu');
      }
      play(o.fuse ? 'omamoriFuse' : 'open');
      await step('shake', 1000);
      // 2. 光がはじける（錬成は煙が立つ）
      if (!o.fuse) play('omikujiPop');
      await step('pop', 500);
      stage.classList.add('opened');
      if (o.fuse) {
        const x = items[0];
        const t = itemTier(x);
        single.hidden = false;
        single.innerHTML = `${lightHtml(t)}<div class="omk-prize t${t}">${prizeArt(x, 'l')}<b>${esc(prizeName(x))}</b>${subHtml(x)}${prizeDesc(x)}</div>`;
        burst(t);
        await step('light', t >= 4 ? 1500 : 900);
        await step('card', 1400);
      } else if (!ten) {
        // 3. 光 → 景品（絵・名前・レア度・効果）
        const x = items[0];
        const t = itemTier(x);
        single.hidden = false;
        single.innerHTML = `${lightHtml(t)}<div class="omk-prize t${t}">${prizeArt(x, 'l')}<b>${esc(prizeName(x))}</b>${subHtml(x)}${prizeDesc(x)}</div>`;
        burst(t);
        await step('light', t >= 4 ? 1500 : t >= 2 ? 900 : 300);
        await step('card', 1600);
      } else {
        // 3. 10枚を並べる → 1枚ずつめくる（最後だけ見る：9枚は一度に開いて最後だけ演出）
        grid.hidden = false;
        grid.innerHTML = items.map(cardHtml).join('');
        await step('grid', 900);
        phase('flip');
        const cards = [...grid.querySelectorAll<HTMLElement>('.omk-card')];
        for (let i = 0; i < cards.length; i++) {
          const t = itemTier(items[i]);
          const quick = skip || (lastOnly && i < cards.length - 1);
          cards[i].classList.add('open');
          if (quick) continue;
          play('omikujiFlip');
          if (t >= 3) burst(t);
          await step('flip', t >= 4 ? 1300 : t === 3 ? 700 : 260);
        }
        if (!skip) await step('flip', 700);
      }
      if (holding) return;
      showResult();
    })();
  });
}

/** 紙吹雪（Canvas、30fps に間引き。粒が無くなったら止まる） */
function confetti(cv: HTMLCanvasElement) {
  const ctx = cv.getContext('2d');
  type P = { x: number; y: number; vx: number; vy: number; r: number; w: number; h: number; c: string; life: number };
  let ps: P[] = [];
  let raf = 0;
  let last = 0;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const size = () => {
    cv.width = Math.round(cv.clientWidth * dpr);
    cv.height = Math.round(cv.clientHeight * dpr);
  };
  const tick = (ts: number) => {
    raf = requestAnimationFrame(tick);
    if (ts - last < 33) return; // 30fps
    const dt = Math.min(0.05, (ts - last) / 1000 || 0.033);
    last = ts;
    if (!ctx) return;
    ctx.clearRect(0, 0, cv.width, cv.height);
    for (const p of ps) {
      p.vy += 900 * dt * dpr;
      p.vx *= 0.985;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.r += 6 * dt;
      p.life -= dt;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.r);
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life * 1.5));
      ctx.fillStyle = p.c;
      ctx.fillRect(-p.w / 2, -p.h / 2 * Math.abs(Math.cos(p.r * 1.7)), p.w, p.h * Math.abs(Math.cos(p.r * 1.7)) + 1);
      ctx.restore();
    }
    ps = ps.filter((p) => p.life > 0 && p.y < cv.height + 40);
    if (!ps.length) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  };
  return {
    burst(n: number, colors: string[]) {
      if (!ctx || n <= 0) return;
      size();
      const cx = cv.width / 2;
      const cy = cv.height * 0.42;
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const v = (400 + Math.random() * 900) * dpr;
        ps.push({ x: cx, y: cy, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 500 * dpr, r: Math.random() * 6, w: (6 + Math.random() * 6) * dpr, h: (10 + Math.random() * 8) * dpr, c: colors[i % colors.length], life: 1.6 + Math.random() * 1.2 });
      }
      if (!raf) {
        last = performance.now();
        raf = requestAnimationFrame(tick);
      }
    },
    stop() {
      cancelAnimationFrame(raf);
      raf = 0;
      ps = [];
    },
  };
}

/** 周回明けのまとめ演出（haul-fx.ts）でも使う */
export { confetti as confettiCanvas };
